import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
  type ShareablePreviewFeeLine,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { majorToMinor, minorToMajor } from "@/core/utils/func/money-minor";
import { useDepositSavingsVault } from "@/features/savings/api/use-deposit-savings-vault";
import { useWithdrawSavingsVault } from "@/features/savings/api/use-withdraw-savings-vault";
import type {
  SavingsMovementRequestDto,
  SavingsVaultDto,
} from "@/features/savings/api/dto";
import {
  createSavingsMovementSchema,
  type SavingsMovementFormValues,
} from "@/features/savings/schemas/savings-vault.schema";
import { useWalletSelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: SavingsMovementFormValues = {
  mainWalletId: "",
  amount: 0,
};

/**
 * Versement (`deposit`) ou retrait (`withdraw`) d'un coffre — idempotent
 * (guide §4.7). Preview `ShareablePreview` avec section frais : **0 par
 * défaut**, mention explicite du produit (`SAVINGS_DEPOSIT` /
 * `SAVINGS_WITHDRAWAL`, gratuits par défaut — frais paramétrables dans
 * fee-engine). Le retrait est refusé (422) tant que le coffre est bloqué.
 */
export function VaultMovementDialog({
  vault,
  kind,
  open,
  onOpenChange,
}: {
  vault: SavingsVaultDto;
  kind: "deposit" | "withdraw";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Signature `(key: string) => string` attendue par les previews.
  const tSavings = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
  const schema = useMemo(() => createSavingsMovementSchema(), [i18n.language]);

  const deposit = useDepositSavingsVault(vault.id);
  const withdraw = useWithdrawSavingsVault(vault.id);
  const mutation = kind === "deposit" ? deposit : withdraw;
  const pending = mutation.isPending;
  const [preview, setPreview] = useState<SavingsMovementRequestDto | null>(
    null
  );
  // Scope du select au propriétaire du coffre — un versement/retrait ne peut
  // transiter que par le portefeuille principal du titulaire (guide §6.10).
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(open, {
      ownerKeycloakUserId: vault.ownerKeycloakUserId ?? null,
    });

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview({
        mainWalletId: parsed.data.mainWalletId,
        amountMinor: majorToMinor(parsed.data.amount, vault.currency),
      });
    },
  });

  const fieldError =
    (name: keyof SavingsMovementFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const title =
    kind === "deposit"
      ? t("savings.vaults.movement.depositTitle")
      : t("savings.vaults.movement.withdrawTitle");

  // Frais : 0 par défaut (produit gratuit par défaut, §6.10) — mention
  // explicite du produit côté retrait comme côté versement.
  const feeLines: ShareablePreviewFeeLine[] = preview
    ? [
        {
          label:
            kind === "deposit"
              ? t("savings.vaults.movement.feeProductDeposit")
              : t("savings.vaults.movement.feeProductWithdrawal"),
          amount: 0,
          currency: vault.currency,
        },
      ]
    : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="mainWalletId"
              validators={{ onChange: fieldError("mainWalletId") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor={`savings-movement-wallet-${kind}`}>
                    {t("savings.vaults.movement.mainWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id={`savings-movement-wallet-${kind}`}
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t(
                      "savings.vaults.movement.mainWalletIdPlaceholder"
                    )}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="amount"
              validators={{ onChange: fieldError("amount") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor={`savings-movement-amount-${kind}`}>
                    {t("savings.vaults.movement.amount")}
                  </Label>
                  <Input
                    id={`savings-movement-amount-${kind}`}
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("savings.vaults.movement.amountPlaceholder")}
                    value={field.state.value || ""}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isNaN(e.target.valueAsNumber)
                          ? 0
                          : e.target.valueAsNumber
                      )
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {kind === "withdraw" && vault.isLocked ? (
              <p className="text-sm text-muted-foreground">
                {t("savings.vaults.movement.lockedWithdrawHint")}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={{
            vaultId: vault.id,
            vaultName: vault.name,
            ...preview,
            currency: vault.currency,
          }}
          title={
            kind === "deposit"
              ? t("savings.vaults.movement.previewDepositTitle")
              : t("savings.vaults.movement.previewWithdrawTitle")
          }
          description={t("savings.vaults.movement.previewDescription")}
          mode="user"
          fees={feeLines}
          total={{
            label: t("savings.vaults.movement.totalLabel"),
            amount: minorToMajor(preview.amountMinor, vault.currency),
            currency: vault.currency,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !pending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: () => {
              if (!preview) return;
              mutation.mutate(preview, {
                onSuccess: () => {
                  notifSilence(
                    kind === "deposit"
                      ? t("savings.vaults.feedback.deposited")
                      : t("savings.vaults.feedback.withdrawn")
                  );
                  setPreview(null);
                  onOpenChange(false);
                },
                onError: (error) =>
                  notifSilenceError(apiErrorHandler(error) || error.message),
              });
            },
            onCancel: () => setPreview(null),
            t: tSavings,
            confirmLoading: pending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
